// Pure domain helpers for cross-app comparison (issue #48).
// Merges two RegionRow arrays (same storefront shape) into a unified structure
// suitable for the dual-app table renderer.

/**
 * A merged row pairing App A and App B data for a single storefront.
 * @typedef {object} ComparisonRow
 * @property {string} countryCode
 * @property {string} countryName
 * @property {import('./normalizeApp.js').RegionRow} appA
 * @property {import('./normalizeApp.js').RegionRow|null} appB
 */

/**
 * Merge two arrays of RegionRows into ComparisonRows, aligned by countryCode.
 * App B rows that have no matching App A row are appended at the end.
 *
 * @param {import('./normalizeApp.js').RegionRow[]} rowsA
 * @param {import('./normalizeApp.js').RegionRow[]} rowsB
 * @returns {ComparisonRow[]}
 */
export function mergeComparisonRows(rowsA, rowsB) {
  const mapB = new Map(rowsB.map((r) => [r.countryCode, r]));
  const seen = new Set();

  const merged = rowsA.map((a) => {
    seen.add(a.countryCode);
    return {
      countryCode: a.countryCode,
      countryName: a.countryName ?? a.countryCode,
      appA: a,
      appB: mapB.get(a.countryCode) ?? null,
    };
  });

  // Append any B-only rows (shouldn't happen with same storefronts, but be safe)
  for (const b of rowsB) {
    if (!seen.has(b.countryCode)) {
      merged.push({ countryCode: b.countryCode, countryName: b.countryName ?? b.countryCode, appA: null, appB: b });
    }
  }

  return merged;
}

/**
 * Sort merged rows so available pairs (both A and B present and available) come
 * first, then partial (one available), then both unavailable.
 * Within each group, sort by App A's ratingCount descending (a rough popularity proxy).
 *
 * @param {ComparisonRow[]} rows
 * @returns {ComparisonRow[]}
 */
export function sortMergedRows(rows) {
  return [...rows].sort((x, y) => {
    const xAvailA = x.appA?.available && !x.appA?.error;
    const xAvailB = x.appB?.available && !x.appB?.error;
    const yAvailA = y.appA?.available && !y.appA?.error;
    const yAvailB = y.appB?.available && !y.appB?.error;

    const xScore = (xAvailA ? 2 : 0) + (xAvailB ? 1 : 0);
    const yScore = (yAvailA ? 2 : 0) + (yAvailB ? 1 : 0);
    if (xScore !== yScore) return yScore - xScore;

    // Within group: sort by App A ratingCount desc
    const aC = x.appA?.userRatingCount ?? -1;
    const bC = y.appA?.userRatingCount ?? -1;
    return bC - aC;
  });
}
